<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900">

  <!-- Sidebar -->
  <HrSidebar :isOpen="isSidebarOpen"/>

  <main class="flex-1 transition-all duration-300" :class="{'ml-64':isSidebarOpen,'ml-0':!isSidebarOpen}">

    <!-- Topbar -->
    <header class="fixed top-0 w-full z-50 bg-white dark:bg-gray-800 shadow">
      <HrTopbar
        :page-title="currentPageTitle"
        :is-sidebar-open="isSidebarOpen"
        :is-dark-mode="isDarkMode"
        @toggle-sidebar="toggleSidebar"
        @toggle-dark-mode="toggleDarkMode"
      />
    </header>

    <div class="p-6 mt-20">

      <!-- User Selection -->
      <div class="flex justify-between items-center mb-8 p-4 bg-white dark:bg-gray-800">
        <div class="flex items-center gap-4">
          <label class="font-medium text-gray-700 dark:text-gray-300">Select User:</label>
          <select
            v-model="selectedUserId"
            @change="loadUserPermissions"
            class="border rounded-lg px-4 py-2.5 w-80 dark:bg-gray-700 dark:text-white focus:outline-none focus:border-[#74bd54]"
          >
            <option value="" disabled>Select a user</option>
            <option v-for="user in users" :key="user.id" :value="user.id">
              {{ user.full_name }}
            </option>
          </select>
        </div>

        <button
          @click="savePermissions"
          :disabled="!selectedUserId"
          class="bg-[#74bd54] text-white px-6 py-2.5 rounded-lg hover:bg-[#63a43f] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          💾 Save Permissions
        </button>
      </div>

      <!-- Permissions Section -->
      <div v-if="selectedUserId" class="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
        <h3 class="text-lg font-semibold text-[#035a76] mb-6 flex items-center gap-2">
          <span>Permissions for</span>
          <span class="text-[#74bd54]">{{ selectedUserName }}</span>
        </h3>

        <div class="grid md:grid-cols-2 gap-8">
          <div v-for="module in modules" :key="module.id" class="border border-gray-200 dark:border-gray-700 rounded-xl p-5">
            <div class="flex items-center justify-between mb-4">
              <h4 class="font-semibold text-gray-800 dark:text-white">{{ module.name }}</h4>
              <label class="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  v-model="module.all"
                  @change="toggleAll(module)"
                  class="w-4 h-4 accent-[#74bd54]"
                />
                <span class="text-gray-600 dark:text-gray-400">All</span>
              </label>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <label v-for="perm in module.permissions" :key="perm.key" class="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="perm.value"
                  class="w-4 h-4 accent-[#74bd54]"
                />
                <span class="text-sm text-gray-700 dark:text-gray-300">{{ perm.label }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Placeholder when no user selected -->
      <div v-else class="text-center py-20 text-gray-400">
        <p class="text-xl">Select a user to manage permissions</p>
      </div>

    </div>
  </main>
</div>
</template>

<script setup>
import { ref, onMounted } from "vue"
import api from "@/services/api"
import HrSidebar from "@/components/hr/HrSidebar.vue"
import HrTopbar from "@/components/hr/HrTopbar.vue"



const currentPageTitle = ref("User Permissions")

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const users = ref([])
const selectedUserId = ref("")
const selectedUserName = ref("")

// Modules will now be loaded from database
const modules = ref([])

// Fetch all modules with their permissions
const fetchModules = async () => {
  try {
    const response = await api.get("Modules.php")
    modules.value = response.data
  } catch (error) {
    console.error("Error fetching modules:", error)
  }
}

// Fetch users
const fetchUsers = async () => {
  try {
    const response = await api.get("Users.php")
    users.value = response.data
  } catch (error) {
    console.error("Error fetching users:", error)
  }
}

// const fetchUsers = async () => {
//   try {
//     const response = await api.get("Users.php");
    
//     users.value = response.data
//       .filter(user => {
//         const type = (user.user_type || user.role || "").trim();
//         return type === "HR" || type.toLowerCase() === "human resources";
//       })
//       .map(user => ({
//         id: user.id,
//         full_name: user.full_name,

//       }));
//   } catch (error) {
//     console.error("Error fetching users:", error);
//     toast.error("Failed to load users");
//   }
// };

// Load permissions for selected user
const loadUserPermissions = async () => {
  if (!selectedUserId.value) return

  const user = users.value.find(u => u.id == selectedUserId.value)
  selectedUserName.value = user ? user.name : ""

  // Reset all checkboxes
  modules.value.forEach(module => {
    module.all = false
    module.permissions.forEach(perm => perm.value = false)
  })

  // TODO: Fetch user's current permissions
  try {
    const res = await api.get(`UserPermissions.php?user_id=${selectedUserId.value}`)
    
    const userPerms = res.data

    modules.value.forEach(module => {
      if (userPerms[module.name]) {
        module.permissions.forEach(perm => {
          if (userPerms[module.name][perm.key] === true) {
            perm.value = true
          }
        })
        
        // Check "All" if all permissions are true
        const allChecked = module.permissions.every(p => p.value === true)
        module.all = allChecked
      }
    })
  } catch (error) {
    console.error("Error loading user permissions:", error)
  }
}

// Toggle "All" checkbox
const toggleAll = (module) => {
  module.permissions.forEach(perm => {
    perm.value = module.all
  })
}

// Save permissions
const savePermissions = async () => {
  if (!selectedUserId.value) return

  const payload = {
    user_id: selectedUserId.value,
    permissions: modules.value.map(module => ({
      module_id: module.id,
      module_name: module.name,
      permissions: module.permissions.reduce((acc, perm) => {
        acc[perm.key] = perm.value
        return acc
      }, {})
    }))
  }

  try {
    await api.post("SavePermissions.php", payload)
    alert("Permissions saved successfully!")
  } catch (error) {
    console.error("Error saving permissions:", error)
    alert("Failed to save permissions")
  }
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  if (isDarkMode.value) {
    document.documentElement.classList.add("dark")
  } else {
    document.documentElement.classList.remove("dark")
  }
}

onMounted(() => {
  fetchUsers()
  fetchModules()        // ← Now loading from DB
})
</script>

<style>
.modal-enter-active,
.modal-leave-active {
  transition: all .25s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(.9);
}
</style>